import type { CanonicalBillingDocument } from "../../canonical/document.ts";
import type { BillingProviderConfigMap } from "../../providers/types.ts";
import type { BillingSqlExecutor } from "../../subject/repository.ts";
import { executeLocalBillingPriceList } from "../local/execute/price-list.ts";
import type { BillingProviderRegistry } from "../local/providers/registry.ts";
import { PROCESS_BILLING_INVOCATION } from "../invocation-authority.ts";
import { resolveBillingCheckoutIntent } from "./checkout-intent.ts";
import { selfDelegatedBillingPrincipal } from "./delegated-principal.ts";
import { createRecurringSubscriptionForPrice } from "./enroll.ts";
import {
	claimCheckoutSessionForSubscriptionAdvance,
	getCheckoutSessionByPayment,
	releaseCheckoutSessionAdvanceClaim,
	updateCheckoutSession,
} from "./enrollment-session.ts";

export async function advanceSelfSubscriptionEnrollment(input: {
	configuredProviders?: BillingProviderConfigMap;
	document: CanonicalBillingDocument;
	registry: BillingProviderRegistry;
	sql: BillingSqlExecutor;
	testMode?: boolean;
}): Promise<void> {
	if (input.document.kind !== "payment") {
		return;
	}
	const session = await getCheckoutSessionByPayment({
		provider: input.document.provider,
		providerPaymentId: input.document.providerPaymentId,
		sql: input.sql,
	});
	if (session == null || session.status === "enrolled") {
		return;
	}
	if (
		input.document.status === "failed" ||
		input.document.status === "canceled"
	) {
		await updateCheckoutSession({
			id: session.id,
			sql: input.sql,
			status:
				input.document.status === "canceled"
					? "first_payment_canceled"
					: "first_payment_failed",
		});
		return;
	}
	if (input.document.status !== "paid") {
		return;
	}
	const claimed = await claimCheckoutSessionForSubscriptionAdvance({
		id: session.id,
		sql: input.sql,
	});
	if (claimed == null) {
		return;
	}
	try {
		const prices = await executeLocalBillingPriceList({
			authority: PROCESS_BILLING_INVOCATION,
			configuredProviders: input.configuredProviders,
			payload: {},
			principal: selfDelegatedBillingPrincipal(
				{
					authenticated: true,
					grants: [],
					rights: [],
					userId: session.subjectId,
				},
				["prices.list"],
			),
			registry: input.registry,
			testMode: input.testMode,
		});
		const price = prices.items.find((item) => item.id === session.priceId);
		if (price == null) {
			await releaseCheckoutSessionAdvanceClaim({
				id: session.id,
				sql: input.sql,
			});
			return;
		}
		const intent = resolveBillingCheckoutIntent(price);
		if (intent.kind !== "recurring") {
			await releaseCheckoutSessionAdvanceClaim({
				id: session.id,
				sql: input.sql,
			});
			return;
		}
		const customerId =
			session.providerCustomerId ?? input.document.providerCustomerId;
		if (customerId == null || customerId.trim() === "") {
			await releaseCheckoutSessionAdvanceClaim({
				id: session.id,
				sql: input.sql,
			});
			return;
		}
		const subscription = await createRecurringSubscriptionForPrice({
			configuredProviders: input.configuredProviders,
			customerId,
			idempotencyKey: session.idempotencyKey,
			interval: intent.interval,
			principal: {
				authenticated: true,
				grants: [],
				rights: [],
				userId: session.subjectId,
			},
			price,
			registry: input.registry,
			sql: input.sql,
			testMode: input.testMode,
		});
		await updateCheckoutSession({
			id: session.id,
			providerCustomerId: customerId,
			providerSubscriptionId: subscription.providerSubscriptionId,
			sql: input.sql,
			status: "enrolled",
		});
	} catch (error) {
		await releaseCheckoutSessionAdvanceClaim({
			id: session.id,
			sql: input.sql,
		});
		throw error;
	}
}
